import React, { useCallback, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { Trash16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, ImagePreviewProps, SimpleInput, SimpleInputProps } from '@makyoapp/makyo-ui'

import { SaveButton } from './SaveButton'

export interface UploadValue {
  value: string
  file: File | null
}

export interface UploadFieldProps {
  id?: string
  className?: string
  value?: UploadValue
  accept?: string

  onChange?: React.ChangeEventHandler<HTMLInputElement>

  validate?: (file: File) => boolean

  onClear?: () => void

  onSubmit?: (value: UploadValue, setIsLoading: (next: boolean) => void, done: () => void, e: React.MouseEvent) => void

  previewUrl?: string | ImagePreviewProps
  inputProps?: SimpleInputProps
  submitLabel?: React.ReactNode
}

export function UploadField(props: UploadFieldProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState<UploadValue>(
    props.value || {
      value: '',
      file: null,
    }
  )
  const inputRef = useRef<HTMLInputElement>(null)

  const [isLoading, setIsLoading] = useBoolean(false)

  const onChange = props.onChange
  const validate = props.validate
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.currentTarget.files?.[0]
      const val = e.currentTarget.value
      if (file) {
        if (validate) {
          if (validate(file)) {
            setValue({ value: val, file })
          } else {
            if (inputRef.current) {
              inputRef.current.value = ''
            }
          }
        } else {
          setValue({ value: val, file })
        }
      }
      onChange?.(e)
    },
    [onChange, validate]
  )

  const done = useCallback(() => {
    setValue({
      value: '',
      file: null,
    })
  }, [setValue])

  return (
    <div className='flex-c'>
      <SimpleInput
        id={props.id}
        {...props.inputProps}
        type='file'
        accept={props.accept}
        ref={inputRef}
        onClear={() => {
          setValue({
            value: '',
            file: null,
          })
          props.onClear?.()
        }}
        showClear={Boolean(value.value)}
        previewUrl={props.previewUrl || value.file?.name}
        placeholder={t('Select File')}
        onChange={handleChange}
        inputClassName={props.className}
        loading={isLoading}
      />
      <SaveButton
        isLoading={isLoading}
        disabled={!value.file}
        onClick={(e) => {
          props.onSubmit?.(value, setIsLoading, done, e)
        }}
        label={props.submitLabel || <Trans>Upload</Trans>}
      />
    </div>
  )
}

export interface UploadFieldMultipleProps {
  id?: string
  className?: string
  value?: File[]
  accept?: string
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  validate?: (file: File) => boolean
  onSubmit?: (value: File[], setIsLoading: (next: boolean) => void, done: () => void, e: React.MouseEvent) => void

  submitLabel?: React.ReactNode
  onRemove?: (files: File[]) => void
}

export function UploadFieldMultiple(props: UploadFieldMultipleProps) {
  const [value, setValue] = useState<File[]>(props.value ?? [])

  const [isLoading, setIsLoading] = useBoolean(false)

  const onChange = props.onChange
  const validate = props.validate
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.currentTarget.files
      if (fileList) {
        if (validate) {
          const validatedValues = Array.from(fileList).filter((f) => {
            return validate(f)
          })
          setValue(validatedValues)
        } else {
          setValue(Array.from(fileList))
        }
      }
      onChange?.(e)
    },
    [onChange, validate]
  )

  const done = useCallback(() => {
    setValue([])
  }, [setValue])

  return (
    <div className={clsx('flex flex-col gap-4', props.className)}>
      {value.length > 0 && (
        <div className='flex flex-col gap-2'>
          {value.map((v, i) => {
            const u = URL.createObjectURL(v)
            return (
              <div key={i} className='flex-c'>
                <div className='flex-cc w-[48px] h-[48px] overflow-hidden'>
                  <img src={u} className='object-contain' onLoad={() => URL.revokeObjectURL(u)} />
                </div>
                <div>{v.name}</div>
                <Button
                  variant={ButtonVariant.Ghost}
                  noPadding
                  squared
                  icon={<Trash16 />}
                  onClick={() => {
                    const nextValue = [...value]
                    nextValue.splice(i, 1)
                    setValue(nextValue)
                    props.onRemove?.(nextValue)
                  }}
                />
              </div>
            )
          })}
        </div>
      )}
      <div className='w-full flex-c'>
        <label className='relative cursor-pointer'>
          <div className='btn-outlined pointer-events-none'>
            <Trans>Select Files</Trans>
          </div>
          <input type='file' className='sr-only' accept={props.accept} multiple onChange={handleChange} />
        </label>
      </div>
      <div
        className='flex-c justify-end px-4 py-4 border-t bdr'
        style={{
          marginInlineStart: '-1rem',
          marginBlockEnd: '-1rem',
          width: 'calc(100% + 2rem)',
        }}
      >
        {/*
           <label className='relative cursor-pointer'>
           <div className='btn-secondary pointer-events-none'>
           <Trans>Select Files</Trans>
           </div>
           <input
           type='file'
           className='sr-only'
           accept={props.accept}
           multiple
           onChange={handleChange}
           />
           </label>
           */}
        <SaveButton
          isLoading={isLoading}
          disabled={!(value.length > 0)}
          onClick={(e) => {
            props.onSubmit?.(value, setIsLoading, done, e)
          }}
          label={props.submitLabel || <Trans>Upload</Trans>}
        />
      </div>
    </div>
  )
}
